import React, { useEffect, useRef, useState } from 'react';

/**
 * The Download control that sits on every report card.
 *
 * One component so the affordance is identical everywhere — the requirement is explicit that the
 * download UI be consistent across cards, and a per-card button would have drifted in label,
 * placement and format list.
 *
 * It takes callbacks rather than the data: each card knows its own columns and its own filtered
 * rows, and passing those through here would make this component the place every report's shape
 * has to be described.
 */
interface ReportDownloadMenuProps {
  onCsv: () => void;
  onPdf: () => void;
  /** Rows the export will contain. Shown so the count is visible before committing to a download. */
  rowCount: number;
  /** Names the report in the button's accessible label, since several sit on one page. */
  label: string;
  disabled?: boolean;
}

const ReportDownloadMenu: React.FC<ReportDownloadMenuProps> = ({
  onCsv,
  onPdf,
  rowCount,
  label,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape — a menu that can only be dismissed by choosing an option
  // traps someone who opened it to check the row count.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (run: () => void) => {
    run();
    setOpen(false);
  };

  const isEmpty = rowCount === 0;

  return (
    <div className="lr-download" ref={wrapRef}>
      <button
        type="button"
        className="lr-download-btn"
        onClick={() => setOpen(o => !o)}
        disabled={disabled || isEmpty}
        aria-haspopup="menu"
        aria-expanded={open}
        title={isEmpty ? `Nothing to download — ${label} has no rows` : `Download ${label} (${rowCount} rows)`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        Download
        <span className="lr-download-count">{rowCount}</span>
      </button>

      {open && (
        <div className="lr-download-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => choose(onCsv)}>
            <strong>CSV / Excel</strong>
            <span>Opens in Excel or Sheets</span>
          </button>
          <button type="button" role="menuitem" onClick={() => choose(onPdf)}>
            <strong>PDF</strong>
            <span>Formatted for printing</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportDownloadMenu;
